import { useState, useRef, useEffect, useMemo } from 'react'
import { formatTimestamp } from '../utils'

export default function SessionDropdown({ sessions, currentSessionId, onSelectSession }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef(null)

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset search when opening/closing
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  const selectedSession = useMemo(() => {
    return sessions.find(s => s.session_id === currentSessionId)
  }, [sessions, currentSessionId])

  const filteredSessions = useMemo(() => {
    if (!searchTerm.trim()) return sessions
    const query = searchTerm.toLowerCase()
    return sessions.filter(s => s.session_id.toLowerCase().includes(query))
  }, [sessions, searchTerm])

  const formatActivityTime = (dateStr) => {
    if (!dateStr) return ''
    return formatTimestamp(dateStr)
  }

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded text-xs transition-all border font-mono select-none"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderColor: 'var(--color-border)',
          color: 'var(--text-primary)',
        }}
      >
        <span className="truncate">
          {selectedSession ? selectedSession.session_id : 'Select a session...'}
        </span>
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute left-0 right-0 mt-1.5 z-50 rounded border shadow-2xl flex flex-col font-mono max-h-[300px]"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--color-border)',
          }}
        >
          {/* Search Input Area */}
          <div className="p-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs rounded outline-none border focus:border-opacity-100 transition-colors"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--text-primary)',
              }}
              autoFocus
            />
          </div>

          {/* Sessions List */}
          <div className="overflow-y-auto flex-1 scrollbar-thin">
            {filteredSessions.length === 0 ? (
              <div className="p-3 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                No sessions found
              </div>
            ) : (
              filteredSessions.map(session => {
                const isSelected = session.session_id === currentSessionId
                const errCount = session.levelSummary?.ERROR || 0
                const warnCount = session.levelSummary?.WARN || 0
                
                return (
                  <button
                    key={session.session_id}
                    onClick={() => {
                      onSelectSession(session.session_id)
                      setIsOpen(false)
                    }}
                    className={`w-full text-left px-3 py-2 flex flex-col gap-1 transition-all border-b last:border-b-0 hover:bg-white/5`}
                    style={{
                      borderColor: 'var(--color-border)',
                      backgroundColor: isSelected ? 'rgba(255,255,255,0.06)' : 'transparent',
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span
                        className={`text-xs font-bold truncate ${isSelected ? 'text-[var(--text-primary)]' : ''}`}
                        style={{ color: isSelected ? 'var(--color-accent)' : 'var(--text-primary)' }}
                      >
                        {session.session_id}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        {formatActivityTime(session.lastActivity)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between w-full text-[10px]">
                      <span style={{ color: 'var(--text-muted)' }}>
                        {session.logCount} log lines
                      </span>
                      
                      <div className="flex items-center gap-1.5">
                        {warnCount > 0 && (
                          <span className="px-1 py-0.2 rounded text-[9px] bg-yellow-500/10 text-yellow-500 font-bold border border-yellow-500/20">
                            W:{warnCount}
                          </span>
                        )}
                        {errCount > 0 && (
                          <span className="px-1 py-0.2 rounded text-[9px] bg-red-500/10 text-red-500 font-bold border border-red-500/20">
                            E:{errCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
